import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStartReport, type ReportPeriod } from '../hooks/use-report.js';
import { useSprint } from '../hooks/use-sprint.js';

// « Report » on a server (operator, 30/10/2026): a period, then a session that writes what happened over it from what
// aidev kept, and that the operator can question or have the report sent. Nothing runs before the click.

const PERIODS: Array<{ key: ReportPeriod; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'days', label: 'The last days' },
  { key: 'week', label: 'This week' },
  { key: 'sprint', label: 'This sprint' },
  { key: 'since-last', label: 'Since my last report' },
];

export function ReportButton({ serverId, running }: { serverId: string; running: boolean }) {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<ReportPeriod>('today');
  // As typed: clamped to 1 to 90 when sent
  const [days, setDays] = useState('7');
  const box = useRef<HTMLDivElement>(null);
  const id = useId();
  const navigate = useNavigate();
  const { data: sprint } = useSprint(serverId);
  const start = useStartReport(serverId);
  const periods = PERIODS.filter((p) => p.key !== 'sprint' || sprint?.configured);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const write = () => start.mutate({ period, ...(period === 'days' ? { days: Math.max(1, Math.min(90, Math.trunc(Number(days)) || 7)) } : {}) }, {
    onSuccess: (r) => navigate(`/servers/${serverId}/sessions/${r.sessionId}`),
  });

  return (
    <div ref={box} className="relative ml-auto">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls={id}
        disabled={!running} title={running ? 'A report of what happened over a period' : 'Start the server to write a report'}
        className="rounded-md border px-3 py-1.5 text-sm font-medium focus-visible:outline focus-visible:outline-2 disabled:opacity-50 max-sm:min-h-10"
        style={{ borderColor: 'var(--color-border-2)', color: 'var(--color-text)', backgroundColor: 'var(--color-surface)' }}>
        Report
      </button>
      {open && (
        <div id={id} role="dialog" aria-label="Report of a period"
          className="absolute right-0 top-full z-50 mt-1 w-72 rounded-lg border p-3 shadow-lg"
          style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
          <fieldset>
            <legend className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>What happened over</legend>
            <div className="mt-2 space-y-1">
              {periods.map((p) => (
                <label key={p.key} className="flex min-h-8 cursor-pointer items-center gap-2 text-sm" style={{ color: 'var(--color-text-2)' }}>
                  <input type="radio" name={`${id}-period`} checked={period === p.key} onChange={() => setPeriod(p.key)} />
                  {p.key === 'days' ? (
                    <span className="flex items-center gap-1.5">
                      The last
                      <input type="number" min={1} max={90} value={days} aria-label="Number of days"
                        onChange={(e) => { setDays(e.target.value); setPeriod('days'); }}
                        className="w-14 rounded-md border px-1.5 py-0.5 text-sm tabular-nums"
                        style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }} />
                      days
                    </span>
                  ) : p.label}
                </label>
              ))}
            </div>
          </fieldset>
          <p className="mt-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
            A session writes it from what aidev kept, then stays for your questions.
          </p>
          {start.isError && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not started: {(start.error as Error).message}</p>}
          <button type="button" onClick={write} disabled={start.isPending}
            className="mt-3 w-full rounded-md px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: 'var(--color-brand)' }}>
            {start.isPending ? 'Starting…' : 'Write the report'}
          </button>
        </div>
      )}
    </div>
  );
}
